import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { quotesApi, usersApi } from "@/lib/api";
import {
  User, Loader2, FileText, Clock, ChevronRight, Plus,
  History, MoreVertical, CheckCircle, XCircle
} from "lucide-react";
import { DashboardHeader, QuoteFilters, QuoteActionsMenu, VersionsModal } from "@/components/Dashboard";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", { 
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit"
  });
};

export default function Dashboard({ onNavigateToQuote, onNewQuote, onBack }) {
  const { isAdmin } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  
  // Load quotes
  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { archived: showArchived };
      if (searchQuery) filters.search = searchQuery;
      if (statusFilter) filters.status = statusFilter;
      if (ownerFilter) filters.owner_id = ownerFilter;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;
      
      const data = await quotesApi.list(filters);
      setQuotes(data);
    } catch (e) {
      console.error("Failed to load quotes", e);
    }
    setLoading(false);
  }, [showArchived, searchQuery, statusFilter, ownerFilter, dateFrom, dateTo]);
  
  // Load users for filter (admin only)
  const loadUsers = useCallback(async () => {
    try {
      const data = await usersApi.listForFilter();
      setUsers(data);
    } catch (e) {
      console.error("Failed to load users", e);
    }
  }, []);
  
  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);
  
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin, loadUsers]);
  
  // Actions
  const handleArchive = async (quoteId, archive) => {
    try {
      await quotesApi.archive(quoteId, archive);
      loadQuotes();
      setActionMenu(null);
    } catch (e) {
      console.error("Failed to archive quote", e);
    }
  };
  
  const handleDelete = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote? This action cannot be undone.")) {
      return;
    }
    try {
      await quotesApi.delete(quoteId);
      loadQuotes();
      setActionMenu(null);
    } catch (e) {
      console.error("Failed to delete quote", e);
    }
  };
  
  const handleCreateVersion = async (quoteId) => {
    try {
      const newQuote = await quotesApi.createNewVersion(quoteId);
      setActionMenu(null);
      onNavigateToQuote(newQuote.id);
    } catch (e) {
      console.error("Failed to create new version", e);
    }
  };
  
  const handleUpdateStatus = async (quoteId, status) => {
    try {
      await quotesApi.updateStatus(quoteId, status);
      loadQuotes();
      setActionMenu(null);
    } catch (e) {
      console.error("Failed to update status", e);
    }
  };
  
  const handleViewVersions = async (quote) => {
    setSelectedQuote(quote);
    setVersionsLoading(true);
    setShowVersions(true);
    try {
      const data = await quotesApi.getVersions(quote.id);
      setVersions(data);
    } catch (e) {
      console.error("Failed to load versions", e);
    }
    setVersionsLoading(false);
  };
  
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setOwnerFilter("");
    setDateFrom("");
    setDateTo("");
  };
  
  const hasActiveFilters = searchQuery || statusFilter || ownerFilter || dateFrom || dateTo;
  
  // Group quotes by client for version display
  const groupedQuotes = useMemo(() => {
    const groups = {};
    quotes.forEach(q => {
      const rootId = q.parentQuoteId || q.id;
      if (!groups[rootId]) {
        groups[rootId] = [];
      }
      groups[rootId].push(q);
    });
    return Object.values(groups).map(group => {
      group.sort((a, b) => (b.version || 1) - (a.version || 1));
      return group[0];
    });
  }, [quotes]);

  const closeVersionsModal = () => {
    setShowVersions(false);
    setSelectedQuote(null);
  };

  const handleVersionNavigate = (id) => {
    onNavigateToQuote(id);
    closeVersionsModal();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <DashboardHeader onBack={onBack} onNewQuote={onNewQuote} />
      
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900 mb-2">Quote Dashboard</h1>
          <p className="text-slate-500">Manage and track all your pricing proposals</p>
        </div>
        
        {/* Filters */}
        <QuoteFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          ownerFilter={ownerFilter}
          setOwnerFilter={setOwnerFilter}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          users={users}
          isAdmin={isAdmin}
          onClearFilters={clearFilters}
        />
        
        {/* Quotes List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#3c68ff]" size={32} />
          </div>
        ) : groupedQuotes.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-state">
            <FileText size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No quotes found</h3>
            <p className="text-slate-500 mb-6">
              {hasActiveFilters 
                ? "Try adjusting your filters or search query" 
                : showArchived 
                  ? "No archived quotes yet" 
                  : "Get started by creating your first quote"}
            </p>
            {!hasActiveFilters && !showArchived && (
              <button
                onClick={onNewQuote}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3c68ff] text-white rounded-lg text-sm font-medium hover:bg-[#2b4bca] transition-all"
              >
                <Plus size={15} />
                Create Quote
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full" data-testid="quotes-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Provider</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Version</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Quote Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last Updated</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedQuotes.map((quote) => {
                  const statusConfig = STATUS_CONFIG[quote.status || "draft"];
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr 
                      key={quote.id} 
                      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                      data-testid={`quote-row-${quote.id}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          data-testid={`open-quote-${quote.id}`}
                          onClick={() => onNavigateToQuote(quote.id)}
                          className="text-left hover:text-[#3c68ff] transition-colors"
                        >
                          <div className="font-medium text-slate-900">{quote.clientName || "Untitled"}</div>
                          <div className="text-xs text-slate-500">{quote.lineItems?.length || 0} line items</div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{quote.currentProvider || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon size={12} />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`view-versions-${quote.id}`}
                          onClick={() => handleViewVersions(quote)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                          <History size={12} />
                          v{quote.version || 1}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#3c68ff]/10 flex items-center justify-center">
                            <User size={12} className="text-[#3c68ff]" />
                          </div>
                          <span className="text-sm text-slate-600">{quote.ownerName || quote.preparedBy || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDate(quote.quoteDate)}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{formatDateTime(quote.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="relative flex justify-center">
                          <button
                            data-testid={`actions-${quote.id}`}
                            onClick={() => setActionMenu(actionMenu === quote.id ? null : quote.id)}
                            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {actionMenu === quote.id && (
                            <QuoteActionsMenu
                              quoteId={quote.id}
                              isArchived={quote.archived}
                              onView={() => onNavigateToQuote(quote.id)}
                              onCreateVersion={() => handleCreateVersion(quote.id)}
                              onUpdateStatus={(status) => handleUpdateStatus(quote.id, status)}
                              onArchive={(archive) => handleArchive(quote.id, archive)}
                              onDelete={() => handleDelete(quote.id)}
                              onClose={() => setActionMenu(null)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
      
      {/* Versions Modal */}
      {showVersions && (
        <VersionsModal
          selectedQuote={selectedQuote}
          versions={versions}
          versionsLoading={versionsLoading}
          onClose={closeVersionsModal}
          onNavigateToQuote={handleVersionNavigate}
        />
      )}
      
      {/* Click outside to close action menu */}
      {actionMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setActionMenu(null)}
        />
      )}
    </div>
  );
}
